import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./order-detail.scss";

const orderDetails = {
  "SB-10084": {
    orderNo: "SB-10084", vendor: "Hikari House", customer: "Channie", phone: "012-3456789", email: "channie@example.com", adminEmail: "admin@secondbite.example.com", status: "Paid", paymentStatus: "Paid", paymentMethod: "Fiuu FPX", collectionTime: "Today, 6:30 PM", orderTime: "28 Jun 2026, 5:58 PM", amount: "RM18.90", subtotal: "RM17.90", serviceFee: "RM1.00", discount: "RM0.00", outlet: "Hikari House - Kuching Central", remarks: "Customer will collect before 6:45 PM.",
    items: [{ name: "Chicken Katsu Bento", qty: 1, price: "RM17.90", total: "RM17.90" }],
    timeline: [["Order Created", "28 Jun 2026, 5:58 PM"], ["Payment Successful", "28 Jun 2026, 6:01 PM"], ["Order Paid", "28 Jun 2026, 6:20 PM"]],
  },
  "SB-10083": {
    orderNo: "SB-10083", vendor: "Green Bowl", customer: "Daniel", phone: "011-2233445", email: "daniel@example.com", adminEmail: "admin@secondbite.example.com", status: "Paid", paymentStatus: "Paid", paymentMethod: "Fiuu Card", collectionTime: "Today, 7:00 PM", orderTime: "28 Jun 2026, 6:10 PM", amount: "RM12.50", subtotal: "RM11.50", serviceFee: "RM1.00", discount: "RM0.00", outlet: "Green Bowl - Kuching", remarks: "Pending vendor preparation.",
    items: [{ name: "Healthy Rice Bowl", qty: 1, price: "RM11.50", total: "RM11.50" }],
    timeline: [["Order Created", "28 Jun 2026, 6:10 PM"], ["Payment Successful", "28 Jun 2026, 6:12 PM"]],
  },
  "SB-10082": {
    orderNo: "SB-10082", vendor: "Daily Bakery", customer: "Mei Ling", phone: "016-9988776", email: "meiling@example.com", adminEmail: "admin@secondbite.example.com", status: "Collected", paymentStatus: "Paid", paymentMethod: "2C2P E-Wallet", collectionTime: "Today, 5:30 PM", orderTime: "28 Jun 2026, 4:45 PM", amount: "RM9.90", subtotal: "RM8.90", serviceFee: "RM1.00", discount: "RM0.00", outlet: "Daily Bakery - Miri", remarks: "Collected by customer.",
    items: [{ name: "Assorted Bread Set", qty: 1, price: "RM8.90", total: "RM8.90" }],
    timeline: [["Order Created", "28 Jun 2026, 4:45 PM"], ["Payment Successful", "28 Jun 2026, 4:46 PM"], ["Order Paid", "28 Jun 2026, 5:10 PM"], ["Collected", "28 Jun 2026, 5:28 PM"]],
  },
};

const cancellationReasons = ["Out of stock", "Kitchen issue", "Store closed unexpectedly", "Product unavailable", "Customer requested cancellation", "Other"];
const timelineSteps = ["Order Created", "Payment Successful", "Order Paid", "Ready for Pickup", "Collected"];
const statusClass = (value) => String(value).toLowerCase().replace(/\s+/g, "-");

const moneyToNumber = (value) => Number(String(value || "0").replace(/[^0-9.-]+/g, "")) || 0;
const formatMoney = (value) => `RM${value.toFixed(2)}`;

const InfoItem = ({ label, value }) => (
  <div className="order-detail__info-item"><span>{label}</span><strong>{value}</strong></div>
);

const OrderDetailPage = () => {
  const navigate = useNavigate();
  const { orderNo } = useParams();
  const sourceOrder = orderDetails[orderNo] || orderDetails["SB-10084"];
  const [order, setOrder] = useState(sourceOrder);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState(cancellationReasons[0]);
  const [cancelRemarks, setCancelRemarks] = useState("");

  const timeline = useMemo(() => order.timeline || [], [order.timeline]);
  const timelineMap = useMemo(() => Object.fromEntries(timeline), [timeline]);
  const canCollect = order.status === "Paid";
  const canCancel = order.status === "Paid";
  const platformFee = order.platformFee || order.serviceFee || "RM0.00";
  const payoutAmount = order.payoutAmount || formatMoney(moneyToNumber(order.amount) - moneyToNumber(platformFee));

  const addTimeline = (title) => {
    const time = new Date().toLocaleString("en-MY", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
    return [...timeline, [title, time]];
  };

  const markCollected = () => setOrder((current) => ({ ...current, status: "Collected", remarks: "Order collected by customer.", timeline: addTimeline("Collected") }));
  const confirmCancellation = () => {
    setOrder((current) => ({
      ...current,
      status: "Cancelled",
      cancellationReason: cancelReason,
      cancellationRemarks: cancelRemarks || "-",
      cancelledBy: "Vendor",
      cancelledAt: new Date().toLocaleString("en-MY", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }),
      remarks: `Order cancelled by vendor. Reason: ${cancelReason}.`,
      timeline: addTimeline("Order Cancelled"),
    }));
    setIsCancelModalOpen(false);
  };

  return (
    <div className="order-detail">
      <section className="order-detail__header">
        <div>
          <button className="order-detail__back" type="button" onClick={() => navigate("/vendor/orders")}>← Back to Orders</button>
          <p className="order-detail__eyebrow">SecondBite Vendor</p>
          <h2>Order {order.orderNo} <span className={`order-detail__status order-detail__status--${statusClass(order.status)}`}>{order.status}</span></h2>
          <p>Review order details, customer information, payment status, and collection progress.</p>
        </div>
        <div className="order-detail__header-actions"><button type="button">Print Receipt</button></div>
      </section>

      <section className="order-detail__summary-grid">
        <InfoItem label="Customer" value={`${order.customer} / ${order.phone}`} />
        <InfoItem label="Vendor" value={order.vendor} />
        <InfoItem label="Collection Time" value={order.collectionTime} />
        <InfoItem label="Total Amount" value={order.amount} />
      </section>

      <section className="order-detail__panel order-detail__timeline-panel">
        <div className="order-detail__panel-title"><h3>Order Timeline</h3></div>
        <div className="order-detail__timeline-bar">
          {timelineSteps.map((step, index) => {
            const isDone = Boolean(timelineMap[step]);
            const isLastDone = isDone && !timelineSteps.slice(index + 1).some((nextStep) => timelineMap[nextStep]);
            return (
              <div className={`order-detail__timeline-step ${isDone ? "is-done" : ""} ${isLastDone ? "is-current" : ""}`} key={step}>
                <div className="order-detail__timeline-line" />
                <div className="order-detail__timeline-dot">✓</div>
                <strong>{step}</strong>
                <span>{timelineMap[step] || "-"}</span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="order-detail__content-grid">
        <div className="order-detail__left-column">
          <div className="order-detail__panel">
            <div className="order-detail__panel-title"><h3>Order Items</h3></div>
            <div className="order-detail__items">
              <div className="order-detail__items-head"><span>Item</span><span>Qty</span><span>Price</span><span>Total</span></div>
              {order.items.map((item) => <div className="order-detail__items-row" key={item.name}><strong>{item.name}</strong><span>{item.qty}</span><span>{item.price}</span><span>{item.total}</span></div>)}
            </div>
            <div className="order-detail__totals">
              <div><span>Subtotal</span><strong>{order.subtotal}</strong></div>
              <div><span>Discount</span><strong>{order.discount}</strong></div>
              <div className="order-detail__grand-total"><span>Grand Total</span><strong>{order.amount}</strong></div>
              <div><span>Platform Fee</span><strong>{platformFee}</strong></div>
              <div className="order-detail__payout-total"><span>Payout Amount</span><strong>{payoutAmount}</strong></div>
            </div>
          </div>
          <div className="order-detail__panel"><div className="order-detail__panel-title"><h3>Customer & Pickup</h3></div><InfoItem label="Customer Email" value={order.email} /><InfoItem label="Outlet" value={order.outlet} /><InfoItem label="Remarks" value={order.remarks} /></div>
        </div>

        <div className="order-detail__right-column">
          <div className="order-detail__panel order-detail__actions-panel">
            <div className="order-detail__panel-title"><h3>Actions</h3></div>
            <div className="order-detail__action-note"><strong>The customer has paid for this order.</strong><span>Mark the order as collected only after the customer has picked it up.</span></div>
            <button className="order-detail__primary-action" type="button" disabled={!canCollect} onClick={markCollected}>Mark as Collected</button>
            <div className="order-detail__danger-zone"><span>Danger Zone</span><p>Cancel only when the order cannot be fulfilled. The customer and admin will be notified by email.</p><button type="button" disabled={!canCancel} onClick={() => setIsCancelModalOpen(true)}>Cancel Order</button></div>
          </div>

          <div className="order-detail__panel"><div className="order-detail__panel-title"><h3>Payment</h3></div><InfoItem label="Payment Status" value={order.paymentStatus} /><InfoItem label="Payment Method" value={order.paymentMethod} /><InfoItem label="Order Time" value={order.orderTime} /></div>

          {order.status === "Cancelled" && <div className="order-detail__panel order-detail__cancelled-panel"><div className="order-detail__panel-title"><h3>Cancellation</h3></div><InfoItem label="Cancelled By" value={order.cancelledBy} /><InfoItem label="Reason" value={order.cancellationReason} /><InfoItem label="Cancelled At" value={order.cancelledAt} /><InfoItem label="Remarks" value={order.cancellationRemarks} /><div className="order-detail__email-note"><strong>Email Notifications</strong><p>Cancellation email sent to customer and admin ({order.adminEmail}).</p></div></div>}
        </div>
      </section>

      {isCancelModalOpen && (
        <div className="order-detail__modal-backdrop" role="presentation">
          <div className="order-detail__modal" role="dialog" aria-modal="true" aria-labelledby="cancel-order-title">
            <div className="order-detail__modal-header"><div><p className="order-detail__eyebrow">Vendor Cancellation</p><h3 id="cancel-order-title">Cancel Order {order.orderNo}</h3></div><button type="button" onClick={() => setIsCancelModalOpen(false)} aria-label="Close cancellation dialog">×</button></div>
            <p className="order-detail__modal-message">This action cannot be undone. The customer and admin will receive a cancellation email.</p>
            <label className="order-detail__field"><span>Cancellation Reason *</span><select value={cancelReason} onChange={(event) => setCancelReason(event.target.value)}>{cancellationReasons.map((reason) => <option key={reason} value={reason}>{reason}</option>)}</select></label>
            <label className="order-detail__field"><span>Remarks</span><textarea rows="4" value={cancelRemarks} onChange={(event) => setCancelRemarks(event.target.value)} placeholder="Add optional remarks for admin review." /></label>
            <div className="order-detail__modal-actions"><button type="button" onClick={() => setIsCancelModalOpen(false)}>Back</button><button type="button" onClick={confirmCancellation}>Confirm Cancellation</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailPage;
